import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { useI18n } from "../contexts/I18nContext";
import { CATEGORIES, TYPE_LABELS } from "../lib/categories";
import {
  CATEGORY_SLUGS,
  SLUG_BY_CATEGORY,
  categoryFromSlug,
  citySlug,
  cityFromSlug,
} from "../lib/slugs";
import ListingCard from "../components/ListingCard";
import { Search, ArrowRight, MapPin, Tag } from "lucide-react";

/**
 * Multi-variant SEO landing page that serves city-, category- and combined
 * pages from a single component. The active variant is decided from the URL
 * params (`city`, `category`) and an optional `mode` ("free" or "auction"
 * for the `/atiduodu` and `/aukcionai` pages).
 *
 * Each variant gets a unique H1, intro paragraph, JSON-LD ItemList and a
 * grid of matching listings. Internal cross-links to sibling pages give
 * Google plenty of paths to crawl from any one landing.
 */
export default function LandingPage({ mode }) {
  const { lang, t } = useI18n();
  const navigate = useNavigate();
  const params = useParams();
  const citySlugParam = params.city || null;
  const categorySlugParam = params.category || null;

  const [cities, setCities] = useState([]);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);

  // Resolve URL slugs against the canonical city / category lists.
  const categoryKey = categoryFromSlug(categorySlugParam);
  const cityName = useMemo(
    () => (citySlugParam ? cityFromSlug(citySlugParam, cities) : null),
    [citySlugParam, cities]
  );
  const categoryMeta = categoryKey
    ? CATEGORIES.find((c) => c.key === categoryKey)
    : null;

  // Load city list once so we can resolve slugs.
  useEffect(() => {
    let alive = true;
    api.get("/cities").then((r) => {
      if (!alive) return;
      // API may return `{cities: [...]}` or a bare array. Handle both.
      const list = Array.isArray(r.data) ? r.data : r.data?.cities || [];
      setCities(list);
    });
    return () => { alive = false; };
  }, []);

  // If the slug params don't resolve to anything real, send the user to /browse.
  useEffect(() => {
    if (cities.length === 0) return;
    if (citySlugParam && !cityName) {
      navigate("/browse", { replace: true });
      return;
    }
    if (categorySlugParam && !categoryKey) {
      navigate("/browse", { replace: true });
    }
  }, [cities.length, citySlugParam, cityName, categorySlugParam, categoryKey, navigate]);

  // Fetch listings matching the active variant.
  useEffect(() => {
    if (citySlugParam && !cityName) return;
    if (categorySlugParam && !categoryKey) return;
    let alive = true;
    setLoading(true);
    const queryParams = { limit: 60 };
    if (cityName) queryParams.city = cityName;
    if (categoryKey) queryParams.category = categoryKey;
    if (mode === "free") queryParams.listing_type = "free";
    if (mode === "auction") queryParams.listing_type = "auction";
    api
      .get("/listings", { params: queryParams })
      .then((r) => { if (alive) setListings(r.data || []); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [cityName, categoryKey, mode, citySlugParam, categorySlugParam]);

  const labels = LABELS[lang] || LABELS.lt;

  // Compose the H1 / intro from active variant.
  const h1 = buildH1(labels, { cityName, categoryMeta, mode });
  const intro = buildIntro(labels, { cityName, categoryMeta, mode });

  // Page-level SEO. We set the document title / description here so search
  // engines see a per-landing title even though the SPA shares one HTML shell.
  useEffect(() => {
    if (!h1) return;
    document.title = `${h1} | StopChlamas`;
    const desc =
      `${intro} ${listings.length} ${labels.listings_word}.`.slice(0, 300);
    setMeta('meta[name="description"]', desc);
    setMeta('meta[property="og:title"]', `${h1} | StopChlamas`);
    setMeta('meta[property="og:description"]', desc);
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute("href", window.location.href);
  }, [h1, intro, listings.length, labels.listings_word]);

  // JSON-LD ItemList — gives Google a structured view of the listings on this page.
  const jsonLd = useMemo(() => {
    if (!listings.length) return null;
    return {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: h1,
      numberOfItems: listings.length,
      itemListElement: listings.slice(0, 20).map((l, i) => ({
        "@type": "ListItem",
        position: i + 1,
        url: `${window.location.origin}/listing/${l.id}`,
        name: l.title,
      })),
    };
  }, [listings, h1]);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8" data-testid="landing-page">
      {jsonLd && (
        <script
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
        />
      )}

      <h1
        className="text-3xl sm:text-4xl lg:text-5xl font-heading font-black tracking-tight text-[#1A1A18]"
        data-testid="landing-h1"
      >
        {h1}
      </h1>
      <p className="mt-3 text-[#4A4A45] max-w-3xl leading-relaxed">{intro}</p>

      {/* Free-mode landings get a prominent CTA to the map view. */}
      {mode === "free" && (
        <div className="mt-4">
          <Link
            to="/atiduodu/zemelapis"
            data-testid="landing-map-cta"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md font-semibold text-sm bg-[#C85A32] text-white hover:bg-[#A84A28] transition-colors"
          >
            🗺️ {labels.show_on_map}
          </Link>
        </div>
      )}

      {/* Cross-link strip: helps users + Google discover sibling landings */}
      <CrossLinks
        labels={labels}
        cityName={cityName}
        categoryKey={categoryKey}
        mode={mode}
        topCities={TOP_CITIES}
      />

      {/* Listings */}
      {loading ? (
        <div className="py-12 text-center text-[#7A7A73]">…</div>
      ) : listings.length === 0 ? (
        <EmptyState labels={labels} />
      ) : (
        <div className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="landing-listings-grid">
          {listings.map((l) => (
            <ListingCard
              key={l.id}
              listing={l}
              onDeleted={(id) => setListings((cur) => cur.filter((x) => x.id !== id))}
            />
          ))}
        </div>
      )}

      {/* Footer-of-page link cloud for SEO */}
      <SeoLinkCloud
        labels={labels}
        cityName={cityName}
        categoryKey={categoryKey}
      />
    </div>
  );
}

// --------------------------------------------------------------------
// Helpers
// --------------------------------------------------------------------

const TOP_CITIES = [
  "Vilnius", "Kaunas", "Klaipėda", "Šiauliai", "Panevėžys", "Alytus",
  "Marijampolė", "Mažeikiai", "Jonava", "Utena",
];

const LABELS = {
  lt: {
    in_city: "mieste",
    all_cities: "visoje Lietuvoje",
    free_label: "atiduodama nemokamai",
    auction_label: "aukcionuose",
    intro_city: "Naršyk visus skelbimus",
    intro_cat: "Skelbimai kategorijoje",
    intro_free: "Atiduodu — pasiimk nemokamai",
    intro_auction: "Aukcionai (24 valandos)",
    explore_cities: "Kiti miestai",
    explore_categories: "Kitos kategorijos",
    explore_modes: "Specialios sekcijos",
    free_section: "Atiduodu",
    auction_section: "Aukcionai 24h",
    sell_in: "Parduodama",
    no_listings_title: "Šioje sekcijoje skelbimų dar nėra",
    no_listings_desc: "Būk pirmas! Įkelk skelbimą — tai 100% nemokama.",
    no_listings_cta: "Įkelti skelbimą",
    listings_word: "skelbimų",
    all_listings: "Visi skelbimai",
    show_on_map: "Žiūrėti žemėlapyje",
  },
  ru: {
    in_city: "в городе",
    all_cities: "по всей Литве",
    free_label: "отдают бесплатно",
    auction_label: "на аукционах",
    intro_city: "Просматривай все объявления",
    intro_cat: "Объявления в категории",
    intro_free: "Отдаю — забери бесплатно",
    intro_auction: "Аукционы (24 часа)",
    explore_cities: "Другие города",
    explore_categories: "Другие категории",
    explore_modes: "Особые разделы",
    free_section: "Отдаю",
    auction_section: "Аукционы 24ч",
    sell_in: "Продаётся",
    no_listings_title: "В этом разделе пока нет объявлений",
    no_listings_desc: "Будь первым! Подать объявление — 100% бесплатно.",
    no_listings_cta: "Подать объявление",
    listings_word: "объявлений",
    all_listings: "Все объявления",
    show_on_map: "Смотреть на карте",
  },
  en: {
    in_city: "in",
    all_cities: "across Lithuania",
    free_label: "given away for free",
    auction_label: "in auctions",
    intro_city: "Browse all listings",
    intro_cat: "Listings in category",
    intro_free: "Free stuff — pick up for free",
    intro_auction: "Auctions (24 hours)",
    explore_cities: "Other cities",
    explore_categories: "Other categories",
    explore_modes: "Special sections",
    free_section: "Free",
    auction_section: "Auctions 24h",
    sell_in: "For sale",
    no_listings_title: "No listings here yet",
    no_listings_desc: "Be the first! Post a listing — it's 100% free.",
    no_listings_cta: "Post a listing",
    listings_word: "listings",
    all_listings: "All listings",
    show_on_map: "View on map",
  },
};

function buildH1(labels, { cityName, categoryMeta, mode }) {
  if (mode === "free") {
    return cityName
      ? `Atiduodu ${labels.in_city} ${cityName}`
      : labels.intro_free;
  }
  if (mode === "auction") {
    return cityName
      ? `Aukcionai ${labels.in_city} ${cityName}`
      : labels.intro_auction;
  }
  if (cityName && categoryMeta) {
    return `${categoryMeta.lt} ${labels.in_city} ${cityName}`;
  }
  if (cityName) {
    return `Skelbimai ${labels.in_city} ${cityName}`;
  }
  if (categoryMeta) {
    return `${labels.intro_cat}: ${categoryMeta.lt}`;
  }
  return labels.all_listings;
}

function buildIntro(labels, { cityName, categoryMeta, mode }) {
  if (mode === "free") {
    return cityName
      ? `Naršyk daiktus, kurie ${labels.free_label} mieste ${cityName}. Susitarki su savininku ir pasiimk nemokamai. Be tarpininkų, be mokesčių.`
      : `Visi nemokami daiktai visoje Lietuvoje – įrankiai, baldai, drabužiai, sodo prekės. Susitark su savininku ir pasiimk nemokamai.`;
  }
  if (mode === "auction") {
    return cityName
      ? `Aktyvūs 24 valandų aukcionai mieste ${cityName}. Daryk savo statymą iki laikrodžio nuliuko.`
      : `Visi aktyvūs 24 valandų aukcionai visoje Lietuvoje. Stebėk laikrodį, daryk statymus.`;
  }
  if (cityName && categoryMeta) {
    return `Visi „${categoryMeta.lt.toLowerCase()}" skelbimai ${cityName} mieste. Naudoti, geros būklės, nemokamas skelbimo įkėlimas. Susisiek su pardavėju tiesiogiai per žinutes.`;
  }
  if (cityName) {
    return `Visi skelbimai ${cityName} mieste — įrankiai, auto detalės, sodo technika, buities prekės ir kita. Tiesiai nuo savininkų. Skelbimo įkėlimas nemokamas.`;
  }
  if (categoryMeta) {
    return `Visi ${categoryMeta.lt.toLowerCase()} skelbimai visoje Lietuvoje. Filtruok pagal miestą, susitark su savininku per žinutes, pasiimk arba susitark dėl pristatymo.`;
  }
  return `Visi aktyvūs skelbimai StopChlamas platformoje — visoje Lietuvoje.`;
}

function EmptyState({ labels }) {
  return (
    <div className="mt-10 bg-[#F0EFEB] border border-[#D1CFC7] rounded-lg p-8 text-center" data-testid="landing-empty">
      <Search className="w-10 h-10 text-[#C85A32] mx-auto"/>
      <h2 className="mt-4 font-heading font-bold text-2xl text-[#1A1A18]">{labels.no_listings_title}</h2>
      <p className="mt-2 text-[#4A4A45]">{labels.no_listings_desc}</p>
      <Link to="/post" className="mt-5 inline-block btn-primary" data-testid="landing-cta-post">
        {labels.no_listings_cta}
      </Link>
    </div>
  );
}

function CrossLinks({ labels, cityName, categoryKey, mode, topCities }) {
  // Show sibling cities when the user is on a city or city+category page.
  // Show sibling categories when the user is on a category or city+category page.
  const showCityRow = !!cityName || !!categoryKey || mode === "free" || mode === "auction";
  const showCategoryRow = !!categoryKey || !!cityName;
  if (!showCityRow && !showCategoryRow) return null;
  return (
    <div className="mt-6 space-y-3" data-testid="landing-cross-links">
      {showCityRow && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-semibold uppercase tracking-[0.18em] text-[#7A7A73] inline-flex items-center gap-1">
            <MapPin className="w-3.5 h-3.5"/> {labels.explore_cities}:
          </span>
          {topCities
            .filter((c) => c !== cityName)
            .slice(0, 8)
            .map((c) => (
              <Link
                key={c}
                to={
                  mode === "free"
                    ? `/atiduodu/${citySlug(c)}`
                    : mode === "auction"
                    ? `/aukcionai`
                    : categoryKey
                    ? `/skelbimai/${citySlug(c)}/${SLUG_BY_CATEGORY[categoryKey]}`
                    : `/skelbimai/${citySlug(c)}`
                }
                className="text-sm px-3 py-1 rounded-full bg-white border border-[#D1CFC7] hover:border-[#C85A32] hover:text-[#C85A32] transition-colors"
              >
                {c}
              </Link>
            ))}
        </div>
      )}
      {showCategoryRow && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-semibold uppercase tracking-[0.18em] text-[#7A7A73] inline-flex items-center gap-1">
            <Tag className="w-3.5 h-3.5"/> {labels.explore_categories}:
          </span>
          {CATEGORIES.filter((c) => c.key !== categoryKey).slice(0, 8).map((c) => (
            <Link
              key={c.key}
              to={
                cityName
                  ? `/skelbimai/${citySlug(cityName)}/${SLUG_BY_CATEGORY[c.key]}`
                  : `/kategorija/${SLUG_BY_CATEGORY[c.key]}`
              }
              className="text-sm px-3 py-1 rounded-full bg-white border border-[#D1CFC7] hover:border-[#C85A32] hover:text-[#C85A32] transition-colors"
            >
              {c.lt}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function SeoLinkCloud({ labels, cityName, categoryKey }) {
  // Bottom-of-page dense link cloud. Builds the full city×category matrix
  // as anchor text. Google crawls these naturally; users mostly scroll past
  // — that's fine, this is for the bots.
  return (
    <section className="mt-16 pt-10 border-t border-[#D1CFC7]" data-testid="landing-link-cloud">
      <h2 className="font-heading font-bold text-xl text-[#1A1A18]">
        {cityName ? `Daugiau iš ${cityName}` : labels.explore_categories}
      </h2>
      <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-x-4 gap-y-2 text-sm">
        {CATEGORIES.map((c) => (
          <Link
            key={c.key}
            to={
              cityName
                ? `/skelbimai/${citySlug(cityName)}/${SLUG_BY_CATEGORY[c.key]}`
                : `/kategorija/${SLUG_BY_CATEGORY[c.key]}`
            }
            className="text-[#4A4A45] hover:text-[#C85A32] inline-flex items-center gap-1"
          >
            <ArrowRight className="w-3 h-3 opacity-60"/> {c.lt}{cityName ? ` ${cityName}` : ""}
          </Link>
        ))}
        {!cityName && TOP_CITIES.slice(0, 8).map((city) => (
          <Link
            key={city}
            to={categoryKey ? `/skelbimai/${citySlug(city)}/${SLUG_BY_CATEGORY[categoryKey]}` : `/skelbimai/${citySlug(city)}`}
            className="text-[#4A4A45] hover:text-[#C85A32] inline-flex items-center gap-1"
          >
            <ArrowRight className="w-3 h-3 opacity-60"/> {categoryKey ? CATEGORIES.find((c) => c.key === categoryKey).lt : "Skelbimai"} {city}
          </Link>
        ))}
      </div>
    </section>
  );
}

function setMeta(selector, value) {
  const el = document.querySelector(selector);
  if (el) el.setAttribute("content", value);
}
